const TAG = 'progress-meter'

function finiteNumber(value: string | null, fallback: number): number {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export function defineProgressMeter(): void {
  if (typeof window === 'undefined' || window.customElements.get(TAG)) return

  class ProgressMeterElement extends HTMLElement {
    static observedAttributes = ['label', 'max', 'value']

    constructor() {
      super()
      this.attachShadow({ mode: 'open' })
    }

    connectedCallback() {
      const root = this.shadowRoot
      if (!root) return
      if (!root.firstChild) {
        root.innerHTML = `
          <style>
            :host { display: block; margin: 12px 0; }
            .meter {
              box-sizing: border-box; padding: 14px; border: 1px solid var(--rich-progress-border, #ccd6df);
              border-radius: var(--rich-progress-radius, 6px); background: var(--rich-progress-bg, #fff);
              color: var(--rich-progress-color, #26323d);
            }
            .row { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 8px; }
            .label { font-weight: 650; }
            .value { color: var(--rich-progress-muted, #687684); font-variant-numeric: tabular-nums; }
            .track { overflow: hidden; height: 10px; border-radius: 999px; background: var(--rich-progress-track, #e7edf2); }
            .bar { height: 100%; border-radius: inherit; background: var(--rich-progress-fill, #3472a9); transition: width 0.2s ease; }
          </style>
          <div class="meter" role="progressbar" aria-valuemin="0">
            <div class="row"><span class="label"></span><span class="value"></span></div>
            <div class="track"><div class="bar"></div></div>
          </div>`
        root.querySelector('.meter')?.addEventListener('dblclick', event => {
          if (!this.closest('[contenteditable="true"]')) return
          event.preventDefault()
          const rawValue = window.prompt('Progress value:', this.getAttribute('value') ?? '0')
          if (rawValue === null || !Number.isFinite(Number(rawValue))) return
          this.setAttribute('value', rawValue)
          this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
        })
      }
      this.syncView()
    }

    attributeChangedCallback() {
      this.syncView()
    }

    private syncView() {
      const root = this.shadowRoot
      if (!root) return
      const max = Math.max(1, finiteNumber(this.getAttribute('max'), 100))
      const value = Math.max(0, Math.min(max, finiteNumber(this.getAttribute('value'), 0)))
      const percent = Math.round((value / max) * 100)
      const meter = root.querySelector<HTMLElement>('.meter')
      const label = root.querySelector<HTMLElement>('.label')
      const valueLabel = root.querySelector<HTMLElement>('.value')
      const bar = root.querySelector<HTMLElement>('.bar')
      if (label) label.textContent = this.getAttribute('label') || 'Progress'
      if (valueLabel) valueLabel.textContent = `${value} / ${max}`
      if (bar) bar.style.width = `${percent}%`
      meter?.setAttribute('aria-valuemax', String(max))
      meter?.setAttribute('aria-valuenow', String(value))
      meter?.setAttribute('aria-valuetext', `${percent}%`)
    }
  }

  window.customElements.define(TAG, ProgressMeterElement)
}
